import { Capacitor } from '@capacitor/core';
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import type { PostHogConfig } from 'posthog-js';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import 'leaflet/dist/leaflet.css';

import '@/i18n/i18n';
import { queryClient } from '@/api/queryClient';
import { NativeUpdateToast, PWAUpdateToast } from '@/components';
import { router } from '@/router/router.tsx';
import { initActiveAccountSync } from '@/store/active-account/active-account.sync';
import { useAuth } from '@/store/auth/auth.store.ts';
import { APP_ENV, initBackendEnvHeader } from '@/utils/appEnv';
import { initDeepLinks } from '@/utils/deep-links';
import { initKeyboardInset } from '@/utils/keyboardInset';

import './index.css';

initBackendEnvHeader();
initActiveAccountSync();
useAuth.getState().initialize();
initDeepLinks();
initKeyboardInset();

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
// Capture in every environment (dev/demo/prod). Sessions are tagged with an
// `environment` super property so they can be filtered apart in the shared project.
const posthogEnabled = !!posthogKey;

// On native (iOS/Android) the CapacitorHttp plugin patches `fetch` and `XHR`
// to route through native HTTP, and it does not faithfully forward PostHog's
// gzip-compressed session-recording snapshot uploads — so the initial full
// snapshot never reaches PostHog and the replay is unplayable ("snapshot never
// reached PostHog"). Sending uncompressed JSON bodies (which CapacitorHttp does
// forward correctly — it's how the app's own API calls work) restores replay.
// Scoped to native so web builds keep compression. See capacitor.config.ts
// (CapacitorHttp.enabled) and src/api/api.ts for related fetch-patch workarounds.
const isNativePlatform = Capacitor.isNativePlatform();

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  opt_out_capturing_by_default: !posthogEnabled,
  disable_compression: isNativePlatform,
  // Record full user sessions for UAT replay. Inputs are masked by default;
  // keep that on so we don't capture credentials / PII keystrokes.
  disable_session_recording: !posthogEnabled,
  session_recording: {
    maskAllInputs: true,
  },
  loaded: (ph) => {
    // Stamp every event & recording with the build environment for filtering.
    ph.register({ environment: APP_ENV });
  },
} satisfies Partial<PostHogConfig>;

createRoot(document.getElementById('root')!).render(
  <PostHogProvider apiKey={posthogEnabled ? posthogKey : ''} options={options}>
    <QueryClientProvider client={queryClient}>
      <PWAUpdateToast />
      <NativeUpdateToast />
      {/* The safe-area inset belongs on TOP only. sonner fans a bare offset string out
          to all four sides, so the notch inset also became the left/right gutter (67px
          on a 14 Pro) and pushed the toast off the right edge of the screen.

          visibleToasts is one more than the queue in @/utils/toast shows, so the
          three queued messages still fit while an update prompt above holds a
          toast of its own outside the queue. */}
      <Toaster
        position="top-center"
        richColors
        expand
        visibleToasts={4}
        gap={12}
        offset={{
          top: 'max(calc(env(safe-area-inset-top, 0px) + 8px), 24px)',
          right: 24,
          bottom: 24,
          left: 24,
        }}
        mobileOffset={{
          top: 'max(calc(env(safe-area-inset-top, 0px) + 8px), 16px)',
          right: 16,
          bottom: 16,
          left: 16,
        }}
      />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </PostHogProvider>,
);
